import React, { useRef, useState } from 'react';
import api from '../services/api';
import { Upload, CheckCircle, Image, Square, File } from 'lucide-react';

/* ── DadosEngenheiroPanel ─────────────────────────────────────────────────── */
export default function DadosEngenheiroPanel({ clienteId, readonly }) {
  const [open, setOpen]                   = useState(false);
  const [dadosEng, setDadosEng]           = useState(null);  // null = não carregado ainda
  const [loadError, setLoadError]         = useState(false);
  const [loading, setLoading]             = useState(false);
  const [uploading, setUploading]         = useState(null);
  const [uploadingCrt, setUploadingCrt]   = useState(false);
  const [savingProto, setSavingProto]     = useState(false);
  const [savingParecer, setSavingParecer] = useState(false);
  const [savingVistoria, setSavingVistoria] = useState(false);
  const [protocolo, setProtocolo]         = useState('');
  const [vistoriaNum, setVistoriaNum]     = useState('');
  const crtRef  = useRef(null);
  const fileRef = useRef({});

  const load = async () => {
    if ((dadosEng !== null && !loadError) || loading) return;
    setLoading(true);
    setLoadError(false);
    try {
      const r = await api.get(`/documentos/dados-engenheiro/${clienteId}/`);
      setDadosEng(r.data);
      setProtocolo(r.data.engenheiro?.protocolo_projeto || '');
      setVistoriaNum(r.data.engenheiro?.pedido_vistoria_numero || '');
    } catch (e) {
      console.error('Erro ao carregar dados de engenharia', e);
      setLoadError(true);
    } finally { setLoading(false); }
  };

  const handleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  const eng = dadosEng?.engenheiro || {};

  const uploadFile = async (tipo, arquivo) => {
    setUploading(tipo);
    try {
      const form = new FormData();
      form.append('tipo', tipo);
      form.append('arquivo', arquivo);
      const resp = await api.post(`/documentos/upload-engenheiro/${clienteId}/`, form);
      setDadosEng(prev => ({ ...prev, engenheiro: { ...prev.engenheiro, [tipo]: resp.data.url } }));
    } catch (e) { alert('Erro ao enviar: ' + (e.response?.data?.error || e.message || 'tente novamente'));
    } finally { setUploading(null); }
  };

  const saveProtocolo = async () => {
    setSavingProto(true);
    try {
      const resp = await api.post(`/documentos/protocolo-engenheiro/${clienteId}/`, { protocolo });
      setDadosEng(prev => ({ ...prev, engenheiro: { ...prev.engenheiro, protocolo_projeto: resp.data.protocolo } }));
    } catch { alert('Erro ao salvar protocolo.');
    } finally { setSavingProto(false); }
  };

  const saveParecer = async (status) => {
    setSavingParecer(true);
    try {
      const resp = await api.post(`/documentos/parecer-status/${clienteId}/`, { status });
      setDadosEng(prev => ({ ...prev, engenheiro: { ...prev.engenheiro, parecer_acesso_status: resp.data.status, parecer_acesso_data: resp.data.data } }));
    } catch (e) {
      alert(e.response?.data?.error || 'Erro ao salvar parecer.');
    } finally { setSavingParecer(false); }
  };

  const saveVistoria = async () => {
    setSavingVistoria(true);
    try {
      const resp = await api.post(`/documentos/vistoria-numero/${clienteId}/`, { numero: vistoriaNum });
      setDadosEng(prev => ({ ...prev, engenheiro: { ...prev.engenheiro, pedido_vistoria_numero: resp.data.numero } }));
    } catch { alert('Erro ao salvar número de vistoria.');
    } finally { setSavingVistoria(false); }
  };

  const uploadCrt = async (arquivo) => {
    const engId = dadosEng?.eng_id;
    if (!engId) { alert('Engenheiro não encontrado. Verifique se o processo de engenharia foi criado.'); return; }
    setUploadingCrt(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      const r = await api.post(`/engenheiro/${engId}/subir-crt/`, fd);
      setDadosEng(prev => ({ ...prev, crt_url: r.data.url }));
    } catch (e) { alert('Erro ao enviar CRT: ' + (e.response?.data?.error || e.message || 'tente novamente'));
    } finally { setUploadingCrt(false); }
  };

  const fmtParecerData = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit', timeZone:'America/Sao_Paulo' });
  };

  const bBtn = (cor, dis) => ({
    display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:7,
    background: dis ? '#1a2035' : cor + '22', color: dis ? '#475569' : cor,
    fontSize:12, fontWeight:600, cursor: dis ? 'not-allowed' : 'pointer',
    border:'1px solid ' + (dis ? '#2d3748' : cor + '44'), whiteSpace:'nowrap',
  });

  const FILE_SLOTS = [
    { field:'diagrama_unifilar', label:'Diagrama unifilar', accept:'image/*,application/pdf' },
    { field:'foto_debitos',      label:'Foto comprovando débitos', accept:'image/*' },
  ];

  return (
    <div style={{ marginTop:16, borderTop:'1px solid #1a2035', paddingTop:16 }}>
      <button
        type="button"
        onClick={handleOpen}
        style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'10px 14px', borderRadius:9, cursor:'pointer',
          background:'rgba(96,165,250,0.07)', border:'1px solid rgba(96,165,250,0.25)',
          color:'#60a5fa', fontSize:13, fontWeight:700 }}
      >
        <span>⚙️ Dados do Engenheiro</span>
        <span style={{ fontSize:11 }}>{open ? '▲ Fechar' : '▼ Abrir'}</span>
      </button>

      {open && (
        <div style={{ marginTop:10, background:'#080c14', border:'1px solid #1a2035', borderRadius:10, padding:'0 16px 12px 16px' }}>
          {loading && <div style={{ fontSize:13, color:'#475569', textAlign:'center', padding:20 }}>Carregando...</div>}
          {!loading && loadError && (
            <div style={{ fontSize:13, color:'#f87171', textAlign:'center', padding:12 }}>
              Erro ao carregar dados. <button onClick={load} style={{ color:'#60a5fa', background:'none', border:'none', cursor:'pointer', fontSize:13, textDecoration:'underline' }}>Tentar novamente</button>
            </div>
          )}
          {!loading && dadosEng && (
            <>
              {/* Protocolo do projeto */}
              <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid #1a2035' }}>
                <span style={{ color: eng.protocolo_projeto ? '#4ade80' : '#475569', display:'flex' }}>{eng.protocolo_projeto ? <CheckCircle size={16}/> : <Square size={16}/>}</span>
                <span style={{ flex:1, fontSize:13, color: eng.protocolo_projeto ? '#e2e8f0' : '#64748b', minWidth:180 }}>Protocolo do projeto</span>
                {readonly
                  ? <span style={{ fontSize:13, color:'#e2e8f0' }}>{eng.protocolo_projeto || '—'}</span>
                  : <>
                      <input type="text" value={protocolo} onChange={e => setProtocolo(e.target.value)} placeholder="Número do protocolo"
                        style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:7, color:'#e2e8f0', fontSize:13, padding:'6px 10px', outline:'none', flex:1, maxWidth:220 }} />
                      <button style={bBtn('#f6c90e', savingProto)} onClick={saveProtocolo} disabled={savingProto}>
                        {savingProto ? 'Salvando...' : 'Salvar'}
                      </button>
                    </>
                }
              </div>

              {/* Parecer de acesso */}
              {(() => {
                const parecerUrl    = eng.parecer_acesso;
                const parecerStatus = eng.parecer_acesso_status;
                const parecerData   = eng.parecer_acesso_data;
                const isImg = parecerUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(parecerUrl);
                return (
                  <div style={{ padding:'10px 0', borderBottom:'1px solid #1a2035' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom: (!parecerStatus && !readonly) ? 8 : 0 }}>
                      <span style={{ color: parecerUrl ? '#4ade80' : '#475569', display:'flex' }}>{parecerUrl ? <CheckCircle size={16}/> : <Square size={16}/>}</span>
                      <span style={{ flex:1, fontSize:13, color: parecerUrl ? '#e2e8f0' : '#64748b' }}>Parecer de acesso</span>
                      {parecerUrl && (
                        <a href={parecerUrl} target="_blank" rel="noopener noreferrer" style={{ ...bBtn('#4ade80'), textDecoration:'none' }}>
                          {isImg ? <Image size={13}/> : <File size={13}/>} Ver
                        </a>
                      )}
                      {!readonly && (
                        <>
                          <button style={bBtn('#f6c90e', uploading === 'parecer_acesso')} onClick={() => uploading !== 'parecer_acesso' && fileRef.current['parecer_acesso']?.click()} disabled={uploading === 'parecer_acesso'}>
                            <Upload size={13}/> {uploading === 'parecer_acesso' ? 'Enviando...' : parecerUrl ? 'Substituir' : 'Enviar'}
                          </button>
                          <input ref={el => { fileRef.current['parecer_acesso'] = el; }} type="file" accept="image/*,application/pdf" style={{ display:'none' }}
                            onChange={e => { if (e.target.files[0]) uploadFile('parecer_acesso', e.target.files[0]); e.target.value=''; }} />
                        </>
                      )}
                    </div>
                    {parecerStatus ? (
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginLeft:28, marginTop:4 }}>
                        <span style={{ fontSize:12, fontWeight:700, padding:'3px 10px', borderRadius:20,
                          background: parecerStatus === 'aprovado' ? 'rgba(74,222,128,0.12)' : 'rgba(248,113,113,0.12)',
                          color: parecerStatus === 'aprovado' ? '#4ade80' : '#f87171',
                          border:'1px solid ' + (parecerStatus === 'aprovado' ? 'rgba(74,222,128,0.3)' : 'rgba(248,113,113,0.3)') }}>
                          {parecerStatus === 'aprovado' ? '✅ Aprovado' : '❌ Reprovado'}
                        </span>
                        {parecerData && <span style={{ fontSize:11, color:'#475569' }}>{fmtParecerData(parecerData)}</span>}
                        {!readonly && (
                          <button style={{ ...bBtn('#64748b'), fontSize:11, padding:'3px 8px' }}
                            onClick={() => setDadosEng(prev => ({ ...prev, engenheiro: { ...prev.engenheiro, parecer_acesso_status: null, parecer_acesso_data: null } }))}>
                            Alterar
                          </button>
                        )}
                      </div>
                    ) : !readonly ? (
                      <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:28, marginTop:4 }}>
                        <span style={{ fontSize:12, color:'#64748b', marginRight:4 }}>Parecer:</span>
                        <button style={{ ...bBtn('#4ade80'), opacity: savingParecer ? 0.6 : 1 }} onClick={() => !savingParecer && saveParecer('aprovado')} disabled={savingParecer}>✅ Aprovado</button>
                        <button style={{ ...bBtn('#f87171'), opacity: savingParecer ? 0.6 : 1 }} onClick={() => !savingParecer && saveParecer('reprovado')} disabled={savingParecer}>❌ Reprovado</button>
                        {savingParecer && <span style={{ fontSize:12, color:'#64748b' }}>Salvando...</span>}
                      </div>
                    ) : null}
                  </div>
                );
              })()}

              {/* Diagrama unifilar + Foto débitos */}
              {FILE_SLOTS.map(s => {
                const url   = eng[s.field];
                const isImg = url && /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
                return (
                  <div key={s.field} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid #1a2035' }}>
                    <span style={{ color: url ? '#4ade80' : '#475569', display:'flex' }}>{url ? <CheckCircle size={16}/> : <Square size={16}/>}</span>
                    <span style={{ flex:1, fontSize:13, color: url ? '#e2e8f0' : '#64748b' }}>{s.label}</span>
                    {url && <a href={url} target="_blank" rel="noopener noreferrer" style={{ ...bBtn('#4ade80'), textDecoration:'none' }}>{isImg ? <Image size={13}/> : <File size={13}/>} Ver</a>}
                    {!readonly && (
                      <>
                        <button style={bBtn('#f6c90e', uploading === s.field)} onClick={() => uploading !== s.field && fileRef.current[s.field]?.click()} disabled={uploading === s.field}>
                          <Upload size={13}/> {uploading === s.field ? 'Enviando...' : url ? 'Substituir' : 'Enviar'}
                        </button>
                        <input ref={el => { fileRef.current[s.field] = el; }} type="file" accept={s.accept} style={{ display:'none' }}
                          onChange={e => { if (e.target.files[0]) uploadFile(s.field, e.target.files[0]); e.target.value=''; }} />
                      </>
                    )}
                  </div>
                );
              })}

              {/* Nº Pedido de Vistoria */}
              <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid #1a2035' }}>
                <span style={{ color: eng.pedido_vistoria_numero ? '#4ade80' : '#475569', display:'flex' }}>{eng.pedido_vistoria_numero ? <CheckCircle size={16}/> : <Square size={16}/>}</span>
                <span style={{ fontSize:13, color: eng.pedido_vistoria_numero ? '#e2e8f0' : '#64748b', minWidth:180 }}>Nº Pedido de Vistoria</span>
                {readonly
                  ? <span style={{ fontSize:13, color:'#e2e8f0', flex:1 }}>{eng.pedido_vistoria_numero || '—'}</span>
                  : <>
                      <input type="text" value={vistoriaNum} onChange={e => setVistoriaNum(e.target.value)} placeholder="Número do pedido"
                        style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:7, color:'#e2e8f0', fontSize:13, padding:'6px 10px', outline:'none', flex:1, maxWidth:220 }} />
                      <button style={bBtn('#4ade80', savingVistoria || vistoriaNum === (eng.pedido_vistoria_numero || ''))} onClick={saveVistoria} disabled={savingVistoria}>
                        {savingVistoria ? 'Salvando...' : 'Salvar'}
                      </button>
                    </>
                }
              </div>

              {/* CRT */}
              <div style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0' }}>
                <span style={{ color: dadosEng.crt_url ? '#4ade80' : '#475569', display:'flex' }}>{dadosEng.crt_url ? <CheckCircle size={16}/> : <Square size={16}/>}</span>
                <span style={{ flex:1, fontSize:13, color: dadosEng.crt_url ? '#e2e8f0' : '#64748b' }}>CRT</span>
                {dadosEng.crt_url && (
                  <a href={dadosEng.crt_url} target="_blank" rel="noopener noreferrer" style={{ ...bBtn('#4ade80'), textDecoration:'none' }}><File size={13}/> Ver CRT</a>
                )}
                {!readonly && (
                  <>
                    <input ref={crtRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }}
                      onChange={e => { if (e.target.files[0]) uploadCrt(e.target.files[0]); e.target.value=''; }} />
                    <button style={bBtn('#f6c90e', uploadingCrt || !dadosEng.eng_id)} onClick={() => !uploadingCrt && crtRef.current?.click()} disabled={uploadingCrt || !dadosEng.eng_id}>
                      <Upload size={13}/> {uploadingCrt ? 'Enviando...' : dadosEng.crt_url ? 'Substituir CRT' : 'Anexar CRT'}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
